import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { FaChevronDown } from 'react-icons/fa';

const SIZES = {
  sm: { height: 28, text: 'tw-text-xs',  padLeft: 10, padRight: 26, icon: 9 },
  md: { height: 36, text: 'tw-text-sm',  padLeft: 12, padRight: 30, icon: 11 },
  lg: { height: 44, text: 'tw-text-base', padLeft: 16, padRight: 36, icon: 12 },
};

/**
 * Native <select> wrapper. Prefer TwDropdown for richer UX —
 * keep this for cases where native a11y is required (e.g. mobile).
 */
const TwSelect = forwardRef(({
  value, onChange, options = [], size = 'sm',
  placeholder, disabled = false, error = '', label = '',
  required = false, className = '', selectClassName = '', children, ...rest
}, ref) => {
  const sz = SIZES[size];

  const selectCls = [
    'tw-w-full tw-appearance-none tw-bg-white tw-border tw-rounded-md tw-font-bold tw-outline-none tw-transition-colors tw-cursor-pointer tw-shadow-none',
    sz.text,
    error
      ? 'tw-border-red-400 focus:tw-border-red-500'
      : 'tw-border-slate-200 hover:tw-border-slate-300 focus:tw-border-slate-900',
    disabled ? 'tw-bg-slate-50 tw-text-slate-400 tw-cursor-not-allowed' : 'tw-text-slate-700',
    selectClassName,
  ].filter(Boolean).join(' ');

  return (
    <div className={`tw-flex tw-flex-col tw-gap-1 ${className}`}>
      {label && (
        <label className="tw-text-xs tw-font-bold tw-text-slate-700">
          {label} {required && <span className="tw-text-red-500">*</span>}
        </label>
      )}

      <div className="tw-relative tw-w-full">
        <select
          ref={ref}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={selectCls}
          style={{
            height: `${sz.height}px`,
            paddingLeft: `${sz.padLeft}px`,
            paddingRight: `${sz.padRight}px`,
            backgroundImage: 'none',
            boxShadow: 'none',
          }}
          {...rest}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {children || options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <div
          className="tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400 tw-pointer-events-none"
          style={{ right: '8px' }}
        >
          <FaChevronDown size={sz.icon} />
        </div>
      </div>

      {error && <p className="tw-text-xs tw-text-red-600 tw-m-0">{error}</p>}
    </div>
  );
});

TwSelect.displayName = 'TwSelect';

TwSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
  })),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  className: PropTypes.string,
  selectClassName: PropTypes.string,
  children: PropTypes.node,
};

export default TwSelect;
